import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';

interface PendingCountResponse {
  pendingCount: number;
  criticalPending: number;
  activeCount: number;
  overdueCount: number;
}

export default function SkoBellBadge() {
  const navigate = useNavigate();
  const [data, setData] = useState<PendingCountResponse | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api
        .get<PendingCountResponse>('/sko/commands/pending-count')
        .then((r) => {
          if (!cancelled) setData(r);
        })
        .catch(() => {});
    };
    load();
    const intervalId = setInterval(load, 30 * 1000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  const pending = data?.pendingCount ?? 0;
  const critical = data?.criticalPending ?? 0;
  const active = data?.activeCount ?? 0;
  const overdue = data?.overdueCount ?? 0;
  const total = pending + active;

  const dotColor =
    critical > 0 || overdue > 0
      ? '#EF4444'
      : pending > 0
      ? '#F59E0B'
      : active > 0
      ? '#10B981'
      : null;

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer transition"
        title="СКО Inbox"
      >
        <i
          className="ri-shield-flash-line text-lg"
          style={{ color: dotColor ?? '#6B6B70' }}
        ></i>
        {total > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center"
            style={{ background: dotColor ?? '#9B9BA4', color: 'white' }}
          >
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}></div>
          <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="p-3 border-b border-gray-100">
              <div className="text-xs font-semibold uppercase text-gray-500">СКО</div>
            </div>
            <div className="p-2">
              {total === 0 && (
                <div className="text-xs text-gray-400 text-center py-3">Немає команд</div>
              )}
              {pending > 0 && (
                <div className="flex items-center justify-between px-2 py-1.5 text-sm">
                  <span className="flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500"></i>
                    Очікують підтвердження
                  </span>
                  <span className="font-mono font-semibold">{pending}</span>
                </div>
              )}
              {critical > 0 && (
                <div className="flex items-center justify-between px-2 py-1.5 text-sm">
                  <span className="flex items-center gap-2">
                    <i className="ri-alarm-warning-line text-red-700"></i>
                    Критичні
                  </span>
                  <span className="font-mono font-semibold text-red-700">{critical}</span>
                </div>
              )}
              {active > 0 && (
                <div className="flex items-center justify-between px-2 py-1.5 text-sm">
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line text-yellow-600"></i>
                    Активні
                  </span>
                  <span className="font-mono font-semibold">{active}</span>
                </div>
              )}
            </div>
            <div className="p-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/sko');
                }}
                className="w-full text-xs text-center px-2 py-1.5 rounded cursor-pointer transition"
                style={{ background: '#0066FF', color: 'white' }}
              >
                Відкрити СКО →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
